const TAG = 'size-and-fit';
const CUSTOM_EDITOR_TAG = 'custom-size-editor';
const SIZE_OPTION = 'size';
const CUSTOM_SIZE = 'custom size';
const STANDARD_SIZE = 'standard size';
const ADD_TO_CART_SELECTOR =
  'button[name="add"], input[name="add"], .add-to-cart-button';

const normalize = (value) => String(value || '').trim().toLowerCase();
const escapeCss = (value) =>
  window.CSS?.escape?.(String(value)) || String(value).replace(/["\\]/g, '\\$&');
const optionsKey = (values) => JSON.stringify((values || []).map(normalize));

class SizeAndFit extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    const config = this.parseConfig();
    if (!config) return;

    this.initialized = true;
    this.config = config;
    this.sectionRoot = this.closest('.shopify-section') || document;
    this.sectionId = this.dataset.sectionId || '';
    this.productId = this.dataset.productId || '';
    this.productFormId = this.dataset.productFormId || '';
    this.initialVariantId = String(this.dataset.initialVariantId || '');

    this.refreshQueued = false;
    this.refreshFrame = 0;
    this.mountFrame = 0;
    this.mountTimer = 0;
    this.customState = {
      isCustom: false,
      editorOpen: false,
      showConfirmed: false,
      confirmedMeasurements: [],
    };

    this.variantEntries = Object.entries(this.config.variants || {});
    this.variantIdByOptions = new Map(
      this.variantEntries
        .filter(([, variant]) => Array.isArray(variant.options))
        .map(([variantId, variant]) => [optionsKey(variant.options), variantId])
    );

    const configuredSizeIndex = Number(this.config.sizeOptionIndex);
    this.sizeOptionIndex =
      this.config.hasSizeOption &&
      Number.isInteger(configuredSizeIndex) &&
      configuredSizeIndex >= 0
        ? configuredSizeIndex
        : (this.config.optionNames || []).findIndex(
            (name) => normalize(name) === SIZE_OPTION
          );

    this.refs = {
      summaryName: this.querySelector('[data-size-fit-summary-name]'),
      descriptionPanel: this.querySelector('[data-size-fit-description-panel]'),
      description: this.querySelector('[data-size-fit-summary-description]'),
      descriptionActions: this.querySelector('[data-size-fit-description-actions]'),
      edit: this.querySelector('[data-size-fit-action="edit"]'),
      tooltip: this.querySelector('[data-size-fit-tooltip]'),
      tooltipTrigger: this.querySelector('[data-size-fit-action="tooltip"]'),
    };

    this.variantDescriptionTemplates = new Map(
      [
        ...this.querySelectorAll(
          'template[data-size-fit-variant-description]'
        ),
      ].map((template) => [
        String(template.dataset.variantId || ''),
        template,
      ])
    );

    this.sizeOptionDescriptionTemplates = new Map(
      [
        ...this.querySelectorAll(
          'template[data-size-fit-option-description]'
        ),
      ].map((template) => [
        normalize(template.dataset.sizeName),
        template,
      ])
    );

    this.onClick = this.onClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSectionChange = this.onSectionChange.bind(this);
    this.onMutations = this.onMutations.bind(this);
    this.onCustomStateChange = this.onCustomStateChange.bind(this);
    this.onRequestStandard = this.onRequestStandard.bind(this);

    this.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    this.addEventListener('custom-size:statechange', this.onCustomStateChange);
    this.addEventListener('custom-size:request-standard', this.onRequestStandard);
    this.sectionRoot.addEventListener('change', this.onSectionChange, true);

    this.observer = new MutationObserver(this.onMutations);
    this.observer.observe(this.sectionRoot, {
      childList: true,
      subtree: true,
    });

    const child = this.querySelector(CUSTOM_EDITOR_TAG);

    if (child) {
      customElements.whenDefined(CUSTOM_EDITOR_TAG).then(() => {
        if (!this.isConnected) return;
        this.connectCustomEditor();
        this.mount();
      });
    }

    if (!this.mount()) {
      this.scheduleMountRetry();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('custom-size:statechange', this.onCustomStateChange);
    this.removeEventListener('custom-size:request-standard', this.onRequestStandard);
    this.sectionRoot?.removeEventListener('change', this.onSectionChange, true);
    this.observer?.disconnect();
    this.clearScheduledWork();
  }

  parseConfig() {
    try {
      return JSON.parse(
        this.querySelector('[data-size-fit-config]')?.textContent || '{}'
      );
    } catch (error) {
      console.error('Size and fit configuration is invalid.', error);
      return null;
    }
  }

  onClick(event) {
    const button = event.target.closest('[data-size-fit-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.sizeFitAction;

    if (action === 'edit') {
      this.closeTooltip();
      this.customEditor?.openEditor?.(true);
    }

    if (action === 'tooltip') {
      event.preventDefault();
      this.toggleTooltip();
    }
  }

  onDocumentClick(event) {
    if (!this.refs.tooltip?.hasAttribute('data-open')) return;
    if (this.refs.tooltip.contains(event.target)) return;

    this.closeTooltip();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.closeTooltip(true);
    }
  }

  toggleTooltip() {
    const shouldOpen = !this.refs.tooltip?.hasAttribute('data-open');

    if (shouldOpen) {
      this.openTooltip();
    } else {
      this.closeTooltip();
    }
  }

  openTooltip() {
    if (!this.refs.tooltip || this.refs.tooltip.hidden) return;

    this.refs.tooltip.dataset.open = '';
    this.refs.tooltipTrigger?.setAttribute('aria-expanded', 'true');
  }

  closeTooltip(returnFocus = false) {
    if (!this.refs.tooltip) return;

    const wasOpen = this.refs.tooltip.hasAttribute('data-open');
    this.refs.tooltip.removeAttribute('data-open');
    this.refs.tooltipTrigger?.setAttribute('aria-expanded', 'false');

    if (returnFocus && wasOpen) {
      this.refs.tooltipTrigger?.focus();
    }
  }

  onCustomStateChange(event) {
    if (!this.contains(event.target)) return;

    this.customState = {
      ...this.customState,
      ...(event.detail || {}),
    };

    this.renderSummary();
  }

  onRequestStandard(event) {
    if (!this.contains(event.target)) return;

    if (this.selectStandardSize()) {
      event.preventDefault();
    }
  }

  onSectionChange(event) {
    const target = event.target;

    if (
      !(target instanceof Element) ||
      !target.closest('variant-picker') ||
      !target.matches('input[data-option-name], select[name^="options["]')
    ) {
      return;
    }

    const targetPicker = target.closest('variant-picker');

    if (this.picker && targetPicker !== this.picker) {
      return;
    }

    const optionName =
      target instanceof HTMLInputElement
        ? target.dataset.optionName
        : target.selectedOptions?.[0]?.dataset.optionName ||
          this.optionNameFromSelect(target);

    const selectingCustom =
      normalize(optionName) === SIZE_OPTION &&
      normalize(target.value) === CUSTOM_SIZE;

    if (selectingCustom || this.state?.isCustom) {
      this.connectCustomEditor();
      this.customEditor?.beginCustomTransition?.();
    }

    this.queueRefresh();
  }

  onMutations(mutations) {
    const formSelector = this.productFormId
      ? `#${escapeCss(this.productFormId)}`
      : '';

    const relevant = mutations.some(({ addedNodes, removedNodes }) =>
      [...addedNodes, ...removedNodes].some((node) => {
        if (!(node instanceof Element)) return false;

        if (
          node.matches('variant-picker') ||
          node.querySelector('variant-picker') ||
          node.matches(ADD_TO_CART_SELECTOR) ||
          node.querySelector(ADD_TO_CART_SELECTOR)
        ) {
          return true;
        }

        if (!formSelector) return false;

        return (
          node.matches(formSelector) ||
          Boolean(node.querySelector(formSelector))
        );
      })
    );

    if (relevant) {
      this.queueRefresh();
    }
  }

  queueRefresh() {
    if (this.refreshQueued) return;

    this.refreshQueued = true;

    queueMicrotask(() => {
      this.refreshQueued = false;
      this.mount();

      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = 0;
        this.mount();
      });
    });
  }

  scheduleMountRetry() {
    this.clearMountRetry();

    this.mountFrame = window.requestAnimationFrame(() => {
      this.mountFrame = 0;

      if (this.mount()) return;

      this.mountTimer = window.setTimeout(() => {
        this.mountTimer = 0;
        this.mount();
      }, 160);
    });
  }

  clearMountRetry() {
    window.cancelAnimationFrame(this.mountFrame);
    window.clearTimeout(this.mountTimer);
    this.mountFrame = 0;
    this.mountTimer = 0;
  }

  clearScheduledWork() {
    this.clearMountRetry();
    window.cancelAnimationFrame(this.refreshFrame);
    this.refreshFrame = 0;
    this.refreshQueued = false;
  }

  mount() {
    this.picker = this.findPicker();
    this.productForm = document.getElementById(this.productFormId);
    this.state = this.resolveState();

    this.connectCustomEditor();
    this.customEditor?.setContext?.({
      variantId: this.state.variantId,
      isCustom: this.state.isCustom,
      form: this.productForm,
    });

    this.renderSummary();
    this.dataset.sizeFitReady = '';

    const pickerReady = !this.config.hasSizeOption || Boolean(this.picker);
    return pickerReady && Boolean(this.productForm);
  }

  connectCustomEditor() {
    const editor = this.querySelector(CUSTOM_EDITOR_TAG);

    if (editor && typeof editor.setContext === 'function') {
      this.customEditor = editor;
    }

    return this.customEditor || null;
  }

  findPicker() {
    const allCandidates = [
      ...this.sectionRoot.querySelectorAll('variant-picker'),
    ];

    if (!allCandidates.length) return null;

    const sizeCandidates = this.config.hasSizeOption
      ? allCandidates.filter((picker) => this.hasSizeOption(picker))
      : [];

    const candidates = sizeCandidates.length
      ? sizeCandidates
      : allCandidates;

    const productInfo = this.closest(
      '[data-product-information], .product-information, .product__info-container'
    );

    const localPicker =
      productInfo && candidates.find((picker) => productInfo.contains(picker));

    if (localPicker) return localPicker;

    return (
      candidates.find((picker) => {
        const sectionMatches =
          !this.sectionId ||
          !picker.dataset.sectionId ||
          picker.dataset.sectionId === this.sectionId;

        const productMatches =
          !this.productId ||
          !picker.dataset.productId ||
          picker.dataset.productId === this.productId;

        return sectionMatches && productMatches;
      }) || candidates[0]
    );
  }

  hasSizeOption(picker) {
    if (!picker) return false;

    const hasRadio = [
      ...picker.querySelectorAll('input[data-option-name]'),
    ].some((input) => normalize(input.dataset.optionName) === SIZE_OPTION);

    const hasSelect = [
      ...picker.querySelectorAll('select[name^="options["]'),
    ].some((select) => {
      const selectedOption = select.selectedOptions?.[0];
      const optionName =
        selectedOption?.dataset.optionName || this.optionNameFromSelect(select);

      return normalize(optionName) === SIZE_OPTION;
    });

    return hasRadio || hasSelect;
  }

  optionNameFromSelect(select) {
    return String(select?.name || '').match(/^options\[(.*)]$/)?.[1] || '';
  }

  currentFormVariantId() {
    return String(
      this.productForm?.querySelector('input[name="id"]')?.value ||
        this.initialVariantId ||
        ''
    );
  }

  selectedValueForOption(optionName, optionIndex) {
    const normalizedName = normalize(optionName);

    const checkedInput = [
      ...this.picker?.querySelectorAll('input[data-option-name]:checked') || [],
    ].find(
      (input) => normalize(input.dataset.optionName) === normalizedName
    );

    if (checkedInput) return checkedInput.value;

    const select = [
      ...this.picker?.querySelectorAll('select[name^="options["]') || [],
    ].find((candidate) => {
      const selectedOption = candidate.selectedOptions?.[0];
      const candidateName =
        selectedOption?.dataset.optionName ||
        this.optionNameFromSelect(candidate);

      return normalize(candidateName) === normalizedName;
    });

    if (select) return select.value;

    const currentVariant = this.config.variants?.[this.currentFormVariantId()];
    const currentValue = currentVariant?.options?.[optionIndex];

    if (String(currentValue || '').trim()) {
      return currentValue;
    }

    const availableValues = new Set(
      this.variantEntries
        .map(([, variant]) => variant.options?.[optionIndex])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    );

    return availableValues.size === 1 ? [...availableValues][0] : '';
  }

  readSelectedOptions() {
    const optionNames = Array.isArray(this.config.optionNames)
      ? this.config.optionNames
      : [];

    return optionNames.map((optionName, optionIndex) =>
      this.selectedValueForOption(optionName, optionIndex)
    );
  }

  resolveVariantId(selectedOptions) {
    if (
      selectedOptions.length &&
      selectedOptions.every((value) => String(value || '').trim())
    ) {
      const matchedVariantId = this.variantIdByOptions.get(
        optionsKey(selectedOptions)
      );

      if (matchedVariantId) return matchedVariantId;
    }

    return this.currentFormVariantId();
  }

  resolveState() {
    const selectedOptions = this.readSelectedOptions();
    const variantId = this.resolveVariantId(selectedOptions);
    const variant = this.config.variants?.[variantId] || {
      options: selectedOptions,
    };

    const resolvedOptions = (this.config.optionNames || []).map(
      (_, optionIndex) =>
        selectedOptions[optionIndex] || variant.options?.[optionIndex] || ''
    );

    const sizeName =
      this.config.hasSizeOption && this.sizeOptionIndex >= 0
        ? resolvedOptions[this.sizeOptionIndex] || ''
        : String(this.config.staticSizeName || '');

    return {
      selectedOptions: resolvedOptions,
      variantId,
      variant,
      sizeName,
      isCustom: normalize(sizeName) === CUSTOM_SIZE,
    };
  }

  renderSummary() {
    const sizeName = this.state?.sizeName || '';
    const isCustom = Boolean(this.state?.isCustom);
    const showConfirmed = isCustom && Boolean(this.customState.showConfirmed);
    let panelVisible = false;

    this.toggleAttribute('data-size-fit-custom-state', isCustom);
    this.toggleAttribute('data-size-fit-confirmed-state', showConfirmed);

    if (isCustom) {
      this.setText(
        this.refs.summaryName,
        this.dataset.confirmedHeading || 'Your measurements'
      );

      this.setTooltipVisible(true);
      panelVisible = true;

      if (showConfirmed) {
        this.renderConfirmedMeasurements(
          this.customState.confirmedMeasurements || []
        );
      } else {
        this.clearDescription();
      }
    } else {
      this.setTooltipVisible(false);
      this.setText(this.refs.summaryName, this.displaySizeName(sizeName));

      const variantDescriptionRendered = this.renderVariantDescription(
        this.state?.variantId
      );
      const optionDescriptionRendered = variantDescriptionRendered
        ? false
        : this.renderSizeOptionDescription(sizeName);

      panelVisible =
        variantDescriptionRendered ||
        optionDescriptionRendered ||
        this.renderPlainDescription(this.descriptionFor(sizeName));
    }

    this.setPanelVisible(panelVisible);

    if (this.refs.descriptionActions) {
      this.refs.descriptionActions.hidden = !showConfirmed;
    }

    if (this.refs.edit) {
      this.refs.edit.hidden = !showConfirmed;
    }
  }

  setTooltipVisible(visible) {
    if (!this.refs.tooltip) return;

    this.refs.tooltip.hidden = !visible;

    if (!visible) {
      this.closeTooltip();
    }
  }

  displaySizeName(sizeName) {
    const normalizedName = normalize(sizeName);

    if (normalizedName === STANDARD_SIZE) return 'Standard';
    if (normalizedName === CUSTOM_SIZE) {
      return this.dataset.confirmedHeading || 'Your measurements';
    }

    return String(sizeName || '').trim();
  }

  clearDescription() {
    this.refs.description?.replaceChildren();
    this.setDescriptionVisible(false);
  }

  renderPlainDescription(value) {
    const text = String(value || '').trim();

    this.refs.description?.replaceChildren();

    if (text) {
      this.refs.description?.append(document.createTextNode(text));
    }

    this.setDescriptionVisible(Boolean(text));
    return Boolean(text);
  }

  renderConfirmedMeasurements(entries) {
    const measurements = (Array.isArray(entries) ? entries : [])
      .map((entry) => ({
        name: String(entry?.name || '').trim(),
        value: String(entry?.value || '').trim(),
      }))
      .filter((entry) => entry.name && entry.value);

    this.refs.description?.replaceChildren();

    if (!measurements.length) {
      this.setDescriptionVisible(false);
      return false;
    }

    const list = document.createElement('ul');
    list.className = 'size-and-fit__confirmed-list';

    measurements.forEach(({ name, value }) => {
      const item = document.createElement('li');
      item.className = 'size-and-fit__confirmed-item';

      const label = document.createElement('strong');
      label.className = 'size-and-fit__confirmed-label';
      label.textContent = `${name}:`;

      const measurementValue = document.createElement('span');
      measurementValue.className = 'size-and-fit__confirmed-value';
      measurementValue.textContent = value;

      item.append(label, document.createTextNode(' '), measurementValue);
      list.append(item);
    });

    this.refs.description?.append(list);
    this.setDescriptionVisible(true);
    return true;
  }

  renderVariantDescription(variantId) {
    const template = this.variantDescriptionTemplates.get(
      String(variantId || '')
    );

    if (!(template instanceof HTMLTemplateElement)) {
      this.clearDescription();
      return false;
    }

    const fragment = template.content.cloneNode(true);
    const hasContent = Boolean(fragment.textContent?.trim());

    if (!hasContent) {
      this.clearDescription();
      return false;
    }

    this.refs.description?.replaceChildren(fragment);
    this.setDescriptionVisible(true);
    return true;
  }

  renderSizeOptionDescription(sizeName) {
    const template = this.sizeOptionDescriptionTemplates.get(
      normalize(sizeName)
    );

    if (!(template instanceof HTMLTemplateElement)) {
      this.clearDescription();
      return false;
    }

    const fragment = template.content.cloneNode(true);
    const hasContent = Boolean(fragment.textContent?.trim());

    if (!hasContent) {
      this.clearDescription();
      return false;
    }

    this.refs.description?.replaceChildren(fragment);
    this.setDescriptionVisible(true);
    return true;
  }

  setDescriptionVisible(visible) {
    if (this.refs.description) {
      this.refs.description.hidden = !visible;
    }
  }

  setPanelVisible(visible) {
    if (this.refs.descriptionPanel) {
      this.refs.descriptionPanel.hidden = !visible;
    }
  }

  descriptionFor(sizeName) {
    if (!sizeName) return '';

    const option = this.config.sizeOptions?.find(
      (item) => normalize(item.name) === normalize(sizeName)
    );

    return typeof option?.description === 'string'
      ? option.description.trim()
      : '';
  }

  setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  selectStandardSize() {
    const radio = [
      ...this.picker?.querySelectorAll('input[data-option-name]') || [],
    ].find(
      (candidate) =>
        normalize(candidate.dataset.optionName) === SIZE_OPTION &&
        normalize(candidate.value) === STANDARD_SIZE
    );

    if (radio instanceof HTMLInputElement) {
      radio.click();
      return true;
    }

    const select = [
      ...this.picker?.querySelectorAll('select[name^="options["]') || [],
    ].find((candidate) => {
      const selectedOption = candidate.selectedOptions?.[0];
      const optionName =
        selectedOption?.dataset.optionName ||
        this.optionNameFromSelect(candidate);

      return normalize(optionName) === SIZE_OPTION;
    });

    if (!(select instanceof HTMLSelectElement)) return false;

    const standardOption = [...select.options].find(
      (option) => normalize(option.value) === STANDARD_SIZE
    );

    if (!standardOption) return false;

    select.value = standardOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, SizeAndFit);
}
